import { z } from "zod";

export const intakePaymentHoldPolicyValues = [
  "disabled",
  "optional",
  "optional_if_urgent",
  "required",
] as const;

export const intakePaymentHoldPolicySchema = z.enum(
  intakePaymentHoldPolicyValues,
);

export type IntakePaymentHoldPolicy = z.infer<
  typeof intakePaymentHoldPolicySchema
>;

type EstimateBucket = "small" | "medium" | "large";

interface IntakePaymentHoldInput {
  estimateBucket?: EstimateBucket | null;
  isCovered: boolean;
  policy?: IntakePaymentHoldPolicy | null;
}

interface IntakePaymentHoldRequirementInput extends IntakePaymentHoldInput {
  isUrgent?: boolean;
}

export function resolveIntakePaymentHoldPolicy(
  policy?: IntakePaymentHoldPolicy | null,
): IntakePaymentHoldPolicy {
  return intakePaymentHoldPolicyValues.includes(
    policy as IntakePaymentHoldPolicy,
  )
    ? (policy as IntakePaymentHoldPolicy)
    : "required";
}

export function canOfferIntakePaymentHold({
  estimateBucket,
  isCovered,
  policy,
}: IntakePaymentHoldInput): boolean {
  if (isCovered) return false;

  const resolvedPolicy = resolveIntakePaymentHoldPolicy(policy);
  if (resolvedPolicy === "disabled") return false;

  return estimateBucket === "small" || estimateBucket === "medium";
}

export function isIntakePaymentHoldRequired({
  estimateBucket,
  isCovered,
  policy,
  isUrgent = false,
}: IntakePaymentHoldRequirementInput): boolean {
  if (
    !canOfferIntakePaymentHold({
      estimateBucket,
      isCovered,
      policy,
    })
  ) {
    return false;
  }

  const resolvedPolicy = resolveIntakePaymentHoldPolicy(policy);

  if (resolvedPolicy === "required") return true;
  if (resolvedPolicy === "optional_if_urgent") return isUrgent;

  return false;
}

export type ProductiveCoverageStatus =
  | "covered"
  | "no_active_service"
  | "unmatched";

/**
 * Commercial path: Productive coverage is recorded for staff, but the
 * public intake session is Turnstile-only and does not prove email
 * ownership. Productive `covered` is therefore provisional and does not
 * skip the card-hold path. T&M / out-of-scope / no open work are never
 * "covered by plan". Unmatched contacts still use AI retainer detection.
 */
export function isIntakeRequestCovered(input: {
  productiveCoverage?: ProductiveCoverageStatus | null;
  requiresPayment?: boolean;
}): boolean {
  if (input.productiveCoverage === "covered") return false;
  if (input.productiveCoverage === "no_active_service") return false;
  if (input.productiveCoverage === "unmatched") {
    return input.requiresPayment === false;
  }
  return false;
}

export type PricingGate =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; policy: IntakePaymentHoldPolicy };

/**
 * Pricing must load before a hold policy is chosen. A missing or failed
 * response is an error, never a stand-in policy: treating it as disabled
 * hides a card the server may still require.
 * A later refetch failure keeps the last successful pricing.
 */
export function resolvePricingGate(input: {
  pricing?: { intakePaymentHoldPolicy?: IntakePaymentHoldPolicy | null } | null;
  isPending: boolean;
}): PricingGate {
  if (input.pricing) {
    return {
      status: "ready",
      policy: resolveIntakePaymentHoldPolicy(input.pricing.intakePaymentHoldPolicy),
    };
  }

  if (input.isPending) return { status: "loading" };
  return { status: "error" };
}

export type CardHoldAction = "skip" | "error" | "confirm";

/**
 * Decide what to do with the card element on submit.
 * A required hold must be complete. An optional hold is skipped when the
 * card is empty, rejected when it is only partly filled, and confirmed
 * when it is complete.
 */
export function resolveCardHoldAction({
  paymentRequired,
  empty,
  complete,
}: {
  paymentRequired: boolean;
  empty: boolean;
  complete: boolean;
}): CardHoldAction {
  if (paymentRequired) {
    return complete ? "confirm" : "error";
  }

  if (empty) return "skip";
  if (complete) return "confirm";
  return "error";
}
